/**
 * AdminStats — reliable, bot-filtered usage analytics.
 * Headline numbers exclude bots/crawlers (tracker v2). Automated + legacy
 * unfiltered counts are shown separately for transparency.
 */
import React, { useEffect, useState } from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowDown, ArrowUp, ExternalLink, Eye, FileText, Mail, MapPin, MousePointerClick, Search, ShieldBan, TrendingUp, Users, Vote } from 'lucide-react';
import api from '../lib/api';
import HelpCallout from './HelpCallout';
import AdminEngagement from './AdminEngagement';
import AdminSearchConsole from './AdminSearchConsole';

const RANGES = [
  { key: '7', label: '7 days' },
  { key: '28', label: '28 days' },
  { key: 'month', label: 'This month' },
  { key: 'custom', label: 'Custom' },
];

function rangeQs(mode, cs, ce) {
  if (mode === '7') return 'days=7';
  if (mode === '28') return 'days=28';
  if (mode === 'month') {
    const now = new Date();
    const start = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    return `start=${start}&end=${now.toISOString().slice(0, 10)}`;
  }
  if (mode === 'custom' && cs && ce) return `start=${cs}&end=${ce}`;
  return 'days=7';
}

const TILES = [
  { k: 'page_views', label: 'Human page views', icon: Eye, deltaKey: 'page_view' },
  { k: 'visits', label: 'Visits (sessions)', icon: Users },
  { k: 'pages_per_visit', label: 'Pages per visit', icon: MousePointerClick },
  { k: 'attraction_views', label: 'Attraction views', icon: TrendingUp, deltaKey: 'attraction_view' },
  { k: 'external_clicks', label: 'Outbound clicks', icon: ExternalLink, deltaKey: 'external_link' },
  { k: 'newsletter_signups', label: 'Newsletter signups', icon: Mail, deltaKey: 'newsletter_signup' },
  { k: 'award_votes', label: 'Award votes', icon: Vote, deltaKey: 'award_vote' },
  { k: 'unique_paths', label: 'Distinct pages viewed', icon: MapPin },
  { k: 'searches', label: 'Searches', icon: Search, deltaKey: 'search' },
  { k: 'itineraries_built', label: 'Itineraries built', icon: FileText, deltaKey: 'itinerary_build' },
];

function MetricTile({ label, icon: Icon, value, delta, period }) {
  const pos = (delta ?? 0) >= 0;
  return (
    <div className="bg-white border border-charcoal/10 rounded-2xl p-5" data-testid={`stats-tile-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
      <div className="flex items-start justify-between mb-3">
        <Icon size={18} className="text-heritage" />
        {delta !== undefined && delta !== null && (
          <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${pos ? 'text-forest' : 'text-heritage'}`}>
            {pos ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
            {Math.abs(delta)}%
          </span>
        )}
      </div>
      <div className="font-display text-3xl mb-1">{value?.toLocaleString?.() ?? value ?? '—'}</div>
      <div className="text-xs text-muted">{label} <span className="text-charcoal/40">· {period}</span></div>
    </div>
  );
}

function TopList({ title, items, formatter }) {
  const max = items?.[0]?.count || 1;
  return (
    <div className="bg-white border border-charcoal/10 rounded-2xl p-5 md:p-6">
      <h3 className="font-display text-lg mb-4">{title}</h3>
      {(!items || items.length === 0) ? (
        <div className="text-muted text-sm py-6 text-center">No data yet.</div>
      ) : (
        <ul className="space-y-2.5">
          {items.map((it, i) => (
            <li key={i} className="text-sm">
              <div className="flex items-baseline justify-between gap-3 mb-1">
                <span className="font-medium truncate min-w-0">{formatter ? formatter(it.value) : it.value}</span>
                <span className="text-charcoal/60 shrink-0 tabular-nums">{it.count.toLocaleString()}</span>
              </div>
              <div className="h-1 bg-stone rounded-full overflow-hidden">
                <div className="h-full bg-heritage" style={{ width: `${(it.count / max) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AdminStats() {
  const [mode, setMode] = useState('7');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [qs, setQs] = useState('days=7');
  const [overview, setOverview] = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [top, setTop] = useState(null);
  const [monthly, setMonthly] = useState(null);

  useEffect(() => {
    if (mode !== 'custom') setQs(rangeQs(mode));
  }, [mode]);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get(`/admin/analytics/overview?${qs}`),
      api.get(`/admin/analytics/timeseries?${qs}&types=page_view,newsletter_signup,attraction_view,external_link`),
      api.get(`/admin/analytics/top?${qs}&limit=8`),
      api.get('/admin/analytics/monthly'),
    ]).then(([o, t, tp, m]) => {
      if (!alive) return;
      setOverview(o.data); setTimeseries(t.data); setTop(tp.data); setMonthly(m.data.items || []);
    });
    return () => { alive = false; };
  }, [qs]);

  const chartData = (() => {
    if (!timeseries?.series?.page_view) return [];
    return timeseries.series.page_view.map((d, i) => ({
      date: d.date.slice(5),
      page_view: d.count,
      bot_view: timeseries.series.page_view_bots?.[i]?.count || 0,
      newsletter_signup: timeseries.series.newsletter_signup?.[i]?.count || 0,
      attraction_view: timeseries.series.attraction_view?.[i]?.count || 0,
      external_link: timeseries.series.external_link?.[i]?.count || 0,
    }));
  })();

  const totals = overview?.totals;
  const w = overview?.window;
  const period = w ? `${w.start} → ${w.end}` : '';
  const cmp = w ? `vs previous ${w.length_days} days (${w.prev_start} → ${w.prev_end})` : '';

  return (
    <div className="max-w-7xl space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-4xl">Usage stats</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex gap-1 bg-white border border-charcoal/15 rounded-lg p-1 text-xs font-semibold" data-testid="stats-range-tabs">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setMode(r.key)}
                data-testid={`stats-range-${r.key}`}
                className={`px-4 py-2 rounded-md transition-colors ${mode === r.key ? 'bg-charcoal text-base' : 'text-charcoal/70 hover:text-charcoal'}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          {mode === 'custom' && (
            <div className="flex items-center gap-1.5 bg-white border border-charcoal/15 rounded-lg p-1.5 text-xs" data-testid="stats-custom-range">
              <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} data-testid="stats-custom-start" className="border border-charcoal/15 rounded-md px-2 py-1.5" />
              <span className="text-charcoal/40">→</span>
              <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} data-testid="stats-custom-end" className="border border-charcoal/15 rounded-md px-2 py-1.5" />
              <button
                onClick={() => { if (customStart && customEnd) setQs(rangeQs('custom', customStart, customEnd)); }}
                data-testid="stats-custom-apply"
                className="bg-charcoal text-base font-semibold px-3 py-1.5 rounded-md"
              >
                Apply
              </button>
            </div>
          )}
        </div>
      </div>
      {w && (
        <div className="text-xs text-muted -mt-3" data-testid="stats-comparison-caption">
          Reporting {period} · every % change shown is {cmp}.
        </div>
      )}

      <HelpCallout
        title="These are your reliable, human-only numbers."
        description="The tracker now filters out search-engine crawlers, AI bots, headless browsers and spam before anything reaches these headline tiles. Visits are counted with an anonymous per-tab session (no cookies, no IP addresses — UK DUAA 2025 statistical-purposes compliant, and anyone choosing 'Essential only' is never tracked). URLs are normalised so filter variations of the same page don't inflate the counts, and every outbound external link on the site is now tracked."
        appearsAt={[
          { label: 'Headline tiles', where: 'humans only, vs. previous equivalent window' },
          { label: 'Filtered traffic panel', where: 'how much automated traffic was excluded — for transparency' },
          { label: 'Monthly table', where: 'calendar-month rollup for reporting' },
        ]}
      />

      {/* Headline tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
        {TILES.map((t) => (
          <MetricTile
            key={t.k}
            label={t.label}
            icon={t.icon}
            value={totals?.[t.k]}
            delta={t.deltaKey ? overview?.deltas?.[t.deltaKey] : undefined}
            period={period}
          />
        ))}
        <div className="bg-forest text-base rounded-2xl p-5" data-testid="stats-tile-conversion">
          <div className="text-xs uppercase tracking-widest text-mustard mb-3">Conversion</div>
          <div className="font-display text-3xl mb-1">{totals?.conversion_rate ?? '—'}%</div>
          <div className="text-xs opacity-80">signups ÷ human page views</div>
        </div>
        <div className="bg-charcoal text-base rounded-2xl p-5" data-testid="stats-tile-filtered">
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-mustard mb-3"><ShieldBan size={13}/> Filtered out</div>
          <div className="font-display text-2xl mb-1">{totals?.bot_views?.toLocaleString() ?? '—'} <span className="text-sm font-body opacity-70">bot views</span></div>
          <div className="text-xs opacity-70">
            {totals?.legacy_views ? `+ ${totals.legacy_views.toLocaleString()} legacy unfiltered views (pre-upgrade, not comparable)` : 'excluded from all headline numbers'}
          </div>
        </div>
      </div>

      {/* Engagement & Conversion — session-based, tracker v3 */}
      <AdminEngagement qs={qs} />

      {/* Google Search Console — search queries + landing pages */}
      <AdminSearchConsole qs={qs} />

      {/* Traffic chart */}
      <div className="bg-white border border-charcoal/10 rounded-2xl p-5 md:p-6">
        <h2 className="font-display text-xl mb-4">Daily traffic — humans vs automated — {period}</h2>
        {chartData.length === 0 ? (
          <div className="text-muted text-sm py-10 text-center">No data yet — visitor actions will populate this chart.</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d8" />
              <XAxis dataKey="date" stroke="#7a7468" fontSize={11} />
              <YAxis stroke="#7a7468" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e5e1d8', fontSize: 13 }} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Line type="monotone" dataKey="page_view" name="Human page views" stroke="#9c4b3a" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="bot_view" name="Bot views (filtered)" stroke="#b0aa9c" strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
              <Line type="monotone" dataKey="attraction_view" name="Attraction views" stroke="#3d5b3a" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="external_link" name="Outbound clicks" stroke="#d4a017" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="newsletter_signup" name="Signups" stroke="#5a8aa6" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Monthly rollup */}
      <div className="bg-white border border-charcoal/10 rounded-2xl p-5 md:p-6" data-testid="stats-monthly-table">
        <h2 className="font-display text-xl mb-4">Monthly report — reliable statistics</h2>
        {(!monthly || monthly.length === 0) ? (
          <div className="text-muted text-sm py-6 text-center">Data will appear from the first full day of tracking.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-charcoal/50 border-b border-charcoal/10">
                  <th className="py-2 pr-4">Month</th>
                  <th className="py-2 pr-4 text-right">Page views</th>
                  <th className="py-2 pr-4 text-right">Visits</th>
                  <th className="py-2 pr-4 text-right">Attraction views</th>
                  <th className="py-2 pr-4 text-right">Outbound clicks</th>
                  <th className="py-2 pr-4 text-right">Signups</th>
                  <th className="py-2 text-right">Award votes</th>
                </tr>
              </thead>
              <tbody>
                {monthly.map((m) => (
                  <tr key={m.month} className="border-b border-charcoal/5 last:border-0">
                    <td className="py-2.5 pr-4 font-semibold">{m.month}</td>
                    <td className="py-2.5 pr-4 text-right tabular-nums">{m.page_views.toLocaleString()}</td>
                    <td className="py-2.5 pr-4 text-right tabular-nums">{m.visits.toLocaleString()}</td>
                    <td className="py-2.5 pr-4 text-right tabular-nums">{m.attraction_views.toLocaleString()}</td>
                    <td className="py-2.5 pr-4 text-right tabular-nums">{m.outbound_clicks.toLocaleString()}</td>
                    <td className="py-2.5 pr-4 text-right tabular-nums">{m.signups.toLocaleString()}</td>
                    <td className="py-2.5 text-right tabular-nums">{m.award_votes.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Outbound clicks leaderboard */}
      {top?.top_outbound?.length > 0 && (
        <div className="bg-white border border-charcoal/10 rounded-2xl p-5 mb-4" data-testid="stats-outbound-leaderboard">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-xl">Outbound clicks · where visitors go next</h3>
            <span className="text-muted text-xs">{period}</span>
          </div>
          <div className="space-y-1.5">
            {top.top_outbound.slice(0, 15).map((row, i) => (
              <div key={`${row.target}-${row.slug}-${i}`} data-testid={`outbound-row-${i}`} className="flex items-center justify-between text-sm py-2 border-b border-charcoal/5 last:border-0">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="bg-stone text-charcoal text-xs font-semibold px-2 py-0.5 rounded-full shrink-0">{row.target}</span>
                  <span className="text-charcoal/60 truncate" title={row.slug}>clicked from {row.slug}</span>
                </div>
                <span className="tabular-nums font-semibold shrink-0 ml-3">{row.count.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top tables */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TopList title="Most-viewed pages" items={top?.top_pages} />
        <TopList title="Most-viewed attractions" items={top?.top_attractions} formatter={(slug) => slug.replace(/-/g, ' ')} />
        <TopList title="Top outbound destinations" items={top?.top_external} />
        <TopList title="Top referrers" items={top?.top_referrers} formatter={(v) => v || 'direct'} />
        <TopList title="Top organic-search landing pages" items={top?.top_landing_organic} />
        <TopList title="Top searches" items={top?.top_searches} />
      </div>
    </div>
  );
}
